import type { ReactNode } from 'react';
import {
  CRITERION_IMPORTANCE_LABELS,
  CRITERION_IMPORTANCES,
  CRITERION_KIND_LABELS,
  MUST_HAVE_CAP,
  type JobCriterionView,
} from '@nest-recruit/shared';
import { looksForWords } from './CriteriaTable';
import styles from './builder.module.css';

/**
 * JOB-13's right column at 1280px and wider: "What the ATS will look for", read from the saved
 * criteria, so it follows each cell as it saves. Weights are shown as worked out (§L30).
 */
export function AtsSummary({ criteria }: { readonly criteria: readonly JobCriterionView[] }): ReactNode {
  return (
    <aside className={styles.summary} aria-labelledby="ats-summary-title">
      <h2 id="ats-summary-title" className={styles.summaryTitle}>
        What the ATS will look for
      </h2>
      {criteria.length === 0 ? (
        <p className={styles.help}>Add screening criteria to see what candidates are ranked on.</p>
      ) : (
        CRITERION_IMPORTANCES.map((importance) => {
          const group = criteria.filter((criterion) => criterion.importance === importance);
          if (group.length === 0) return null;
          return (
            <section key={importance} className={styles.summaryGroup}>
              <h3 className={styles.summaryGroupTitle}>{CRITERION_IMPORTANCE_LABELS[importance]}</h3>
              <ul className={styles.summaryList}>
                {group.map((criterion) => (
                  <li key={criterion.id} className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>
                      {criterion.label}
                      <span className={styles.summaryWeight}>{criterion.weight}</span>
                    </span>
                    <span className={styles.summaryMeta}>
                      {CRITERION_KIND_LABELS[criterion.kind]}
                      {criterion.minLevel !== null && ` · at least ${criterion.minLevel}`}
                      {looksForWords(criterion.kind) && criterion.lookFor.length > 0 && ` · ${criterion.lookFor.join(', ')}`}
                      {looksForWords(criterion.kind) && criterion.partlyCounts.length > 0 && ` · partly: ${criterion.partlyCounts.join(', ')}`}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          );
        })
      )}
      {criteria.some((criterion) => criterion.importance === 'must_have') && (
        <p className={styles.help}>Missing a Must-have caps a candidate at {MUST_HAVE_CAP} and flags them. A person still decides.</p>
      )}
    </aside>
  );
}
